import { useMemo } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { Alert, Button, Card, CardBody } from '../ui'
import AiRunnerSetupStep from './AiRunnerSetupStep'
import ApplianceDownloadSetupStep from './ApplianceDownloadSetupStep'
import BootstrapSetupStep from './BootstrapSetupStep'
import EntraSetupStep from './EntraSetupStep'
import MfaSetupStep from './MfaSetupStep'
import TimezoneSetupStep from './TimezoneSetupStep'
import VanitySetupStep from './VanitySetupStep'
import { setupIndexForPath, setupPathForIndex } from './setupRoutes'

const REQUIRED_STEPS = [
  { key: 'entra_id', label: 'Microsoft Entra ID' },
  { key: 'timezone', label: 'Timezone' },
  { key: 'vanity_domains', label: 'Domains' },
  { key: 'mfa_provider', label: 'MFA provider' },
  { key: 'appliance_download', label: 'Appliance download' },
  { key: 'encryption_bootstrap', label: 'Appliance initialization' },
  { key: 'ai_runner_v2', label: 'AI Runner v2' },
]

function statusLabel(key, state) {
  if (key === 'mfa_provider' && state?.decision?.disposition === 'skipped') return 'Skipped'
  if (state?.status === 'complete') return 'Complete'
  if (state?.status === 'action_required') return 'Required'
  if (state?.status === 'unavailable') return 'Unavailable'
  return 'Locked'
}

/** Full-screen, ordered setup boundary; the product shell is not mounted. */
export default function SetupScreen({
  snapshot,
  onRefresh,
  onApplyTimezone,
  onCreateVanityDomains,
  onDeleteVanityDomain,
  onConfigureMfaProvider,
  onVerifyMfaProvider,
  onSkipMfaProvider,
  onRequestApplianceDownload,
  onCreateApplianceEnrollmentCode,
  onStartApplianceBootstrap,
  onApplyAiRunner,
  onFinishSetup,
  reconnecting = false,
}) {
  const location = useLocation()
  const navigate = useNavigate()
  const canManage = snapshot?.setup?.can_manage === true
  const stepState = useMemo(
    () => new Map((snapshot?.setup?.steps || []).map((step) => [step.key, step])),
    [snapshot?.setup?.steps],
  )
  const activeIndex = setupIndexForPath(location.pathname) ?? 1
  const navigateToStep = (index) => navigate(setupPathForIndex(index), { state: location.state })
  const step = (key) => stepState.get(key)

  const renderStep = () => {
    if (activeIndex === 0) return (
      <EntraSetupStep
        step={step('entra_id')}
        onNext={() => navigateToStep(1)}
      />
    )
    if (activeIndex === 1) return (
      <TimezoneSetupStep
        step={step('timezone')}
        onApply={onApplyTimezone}
        onRefresh={onRefresh}
        onNext={() => navigateToStep(2)}
      />
    )
    if (activeIndex === 2) return (
      <VanitySetupStep
        step={step('vanity_domains')}
        onBack={() => navigateToStep(1)}
        onNext={() => navigateToStep(3)}
        onCreate={onCreateVanityDomains}
        onDelete={onDeleteVanityDomain}
        onRefresh={onRefresh}
      />
    )
    if (activeIndex === 3) return (
      <MfaSetupStep
        step={step('mfa_provider')}
        onBack={() => navigateToStep(2)}
        onNext={() => navigateToStep(4)}
        onConfigure={onConfigureMfaProvider}
        onVerifyExisting={onVerifyMfaProvider}
        onSkip={onSkipMfaProvider}
      />
    )
    if (activeIndex === 4) return (
      <ApplianceDownloadSetupStep
        step={step('appliance_download')}
        onBack={() => navigateToStep(3)}
        onNext={() => navigateToStep(5)}
        onDownload={onRequestApplianceDownload}
        onCreateEnrollment={onCreateApplianceEnrollmentCode}
        onRefresh={onRefresh}
      />
    )
    if (activeIndex === 5) return (
      <BootstrapSetupStep
        step={step('encryption_bootstrap')}
        onBack={() => navigateToStep(4)}
        onNext={() => navigateToStep(6)}
        onStart={onStartApplianceBootstrap}
        onRefresh={onRefresh}
      />
    )
    if (activeIndex === 6) return (
      <AiRunnerSetupStep
        step={step('ai_runner_v2')}
        onBack={() => navigateToStep(5)}
        onRefresh={onRefresh}
        onApply={onApplyAiRunner}
        onFinish={onFinishSetup}
      />
    )
    return null
  }

  const stepAvailable = (index) => (
    (index === 0 && step('entra_id')?.status === 'complete')
    || index === 1
    || (index === 2 && step('timezone')?.status === 'complete')
    || (index === 3 && step('vanity_domains')?.status === 'complete')
    || (index === 4 && step('mfa_provider')?.status === 'complete')
    || (index === 5 && step('appliance_download')?.status === 'complete')
    || (index === 6 && step('encryption_bootstrap')?.status === 'complete')
  )

  return (
    <main className="min-h-screen bg-[var(--app-bg)] p-4 md:p-8" data-testid="workspace-setup-screen">
      <div className="mx-auto max-w-5xl space-y-4">
        <header className="space-y-2 py-4">
          <p className="text-xs font-medium uppercase tracking-[0.16em] text-[var(--app-accent)]">Workspace setup</p>
          <h1 className="text-2xl font-medium text-[var(--app-text)]">
            {canManage ? 'Complete setup to unlock Operations' : 'Your workspace is being set up'}
          </h1>
          <p className="text-sm text-[var(--app-text-muted)]">
            {canManage
              ? 'Operations remains locked until every required step is complete and you finish setup.'
              : 'A Global Administrator is completing the required setup. This screen updates automatically.'}
          </p>
        </header>

        {reconnecting && <Alert color="warning">Live setup status is reconnecting. The last verified state remains displayed.</Alert>}

        {canManage && (
          <div className="grid gap-4 md:grid-cols-[280px_minmax(0,1fr)]">
            <Card>
              <CardBody>
                <ol className="space-y-1" aria-label="Required setup sequence">
                  {REQUIRED_STEPS.map((item, index) => (
                    <li key={item.key}>
                      <button
                        type="button"
                        disabled={!stepAvailable(index)}
                        onClick={() => navigateToStep(index)}
                        className={`flex w-full items-center gap-3 rounded-[var(--app-radius)] px-2 py-2 text-left text-sm ${activeIndex === index ? 'bg-[var(--app-surface-2)]' : ''} disabled:opacity-55`}
                      >
                        <span className="flex size-7 shrink-0 items-center justify-center rounded-full border border-[var(--app-border-strong)] text-xs font-medium">{index + 1}</span>
                        <span className="min-w-0 flex-1 text-[var(--app-text)]">{item.label}</span>
                        <span className="text-[10px] uppercase tracking-wide text-[var(--app-text-muted)]">{statusLabel(item.key, step(item.key))}</span>
                      </button>
                    </li>
                  ))}
                </ol>
              </CardBody>
            </Card>
            <Card><CardBody>{renderStep()}</CardBody></Card>
          </div>
        )}

        <div className="flex justify-end">
          <Button size="sm" onClick={onRefresh}>Refresh status</Button>
        </div>
      </div>
    </main>
  )
}
